import { ChangeDetectionStrategy, Component, HostListener, Signal, computed, input, signal } from '@angular/core';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { LayoutStyles } from 'lib/ui/layout/layout-ui.service.js';
import { AppTheme } from 'lib/ui/theme/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { CoreAppComponent } from '../../core/core-app.component.js';
import { IPageMetadata, ITypeParentRoute } from '../../pages/type-routes/type-route.models.js';
import { ILayoutMenuItem } from '../layout.models.js';

export type LayoutToolbarType = 'default' | 'authPage';

@Component({
	selector: 'layout-toolbar-component',
	templateUrl: './toolbar.component.html',
	styleUrls: ['./toolbar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ToolbarComponent extends CoreAppComponent {
	public readonly toolbarType = input.required<LayoutToolbarType>();
	public readonly title = input<string>();
	public readonly subtitle = input<string>();
	public readonly avatar = input<IImage>();
	public readonly buttons = input<readonly IButton[]>();
	public readonly parentRoutes = input<readonly ITypeParentRoute[]>();
	public readonly layoutMenuItems = input.required<readonly ILayoutMenuItem[]>();

	public readonly showSignupButton = computed<boolean>(() => {
		return this.registeredSubdomain() ? false : true;
	});

	public readonly calculatedAvatarImage = computed<IImage | undefined>(() => {
		const avatar = this.avatar();

		if (avatar) {
			return avatar;
		}

		return this.brandLogoImage();
	});

	public readonly toolbarSizeClass = computed<string | undefined>(() => {
		return `size-${this.toolbarType()}`;
	});

	public readonly userMenuTitle = computed<string | undefined>(() => {
		const user = this.authUser();

		if (!user) {
			return undefined;
		}

		return nameHelper.getUserName(user.email, user.firstName, user.lastName);
	});

	public readonly userMenuSubtitle = computed<string | undefined>(() => {
		const user = this.authUser();

		if (!user) {
			return undefined;
		}

		if (user.email === this.userMenuTitle()) {
			return user.userType === 'client' ? 'Client' : 'Trainer';
		}

		return user.email;
	});

	public readonly layoutSizeStyles = computed<LayoutStyles>(() => {
		if (this.toolbarType() === 'default') {
			return this.dependencies.layoutUiService.getLayoutSizeStyles({
				screenService: this.dependencies.screenService,
				type: 'large'
			});
		}
		return {};
	});

	public readonly showTrialButton = computed<boolean>(() => {
		const authUser = this.authUser();

		if (!authUser) {
			return false;
		}

		if (authUser.license !== 'premium' && authUser.userType === 'trainer') {
			return true;
		}

		return false;
	});

	public readonly showLicenceTag = computed<boolean>(() => {
		return (
			!this.showTrialButton() && this.authUser()?.userType === 'trainer' && this.authUser()?.license === 'premium'
		);
	});

	public readonly upgradeRouterLink: string = this.dependencies.navigateService
		.actionRoute((routes) => routes.profileRoutes.view.children.subscription)
		.getUrl();
	public readonly loginRouterLink: string = this.dependencies.navigateService.loginPage().getUrl();
	public readonly signupRouterLink: string = this.dependencies.navigateService.signupPage().getUrl();

	public readonly metadata: Signal<IPageMetadata | undefined> = this.dependencies.sharedService.pageMetadata;

	public readonly isScrolled = signal<boolean>(false);

	public readonly trialButtonText = computed<string>(() => {
		if (this.trialState().daysRemaining === 0) {
			return `Upgrade`;
		}

		return `${this.trialState().daysRemaining} days left`;
	});

	public readonly desktopMenuItems = computed<ILayoutMenuItem[]>(() => {
		return [...(this.layoutMenuItems() ?? [])];
	});

	public readonly mobileMenuItems = computed<ILayoutMenuItem[]>(() => {
		return [...this.desktopMenuItems()];
	});

	public readonly parentRoute = computed<ITypeParentRoute | undefined>(() => {
		const parentRoutes = (this.parentRoutes() ?? []).filter((m) => m.routerLink !== this.dependencies.router.url);
		if (parentRoutes?.length) {
			const parentRoute = parentRoutes[parentRoutes.length - 1];

			if (parentRoute.routerLink !== this.dependencies.router.url) {
				return parentRoute;
			}
		}
		return undefined;
	});

	@HostListener('window:scroll', ['$event'])
	isScrolledIntoView() {
		let isScrolled: boolean;
		if (window.scrollY > 0) {
			isScrolled = true;
		} else {
			isScrolled = false;
		}

		this.isScrolled.set(isScrolled);
	}

	constructor() {
		super();
	}

	handleSetTheme(theme: AppTheme): void {
		this.dependencies.themeService.setTheme(theme);
	}

	handleFeedMenuOpened(): void {
		this.dependencies.layoutService.markFeedsAsRead();
	}
}
